import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
// import { HOST_API } from 'src/config-global';
import { Company } from 'src/types/data';
import { setSelectedCompanyIds } from './dataSlice';

// Interfaces
export interface Transfer {
  id: number;
  company_id: number | null;
  type: number; // 1 for National, 2 for International
  numero: string;
  date_d_emition: string; // Format: YYYY-MM-DD
  bank_id: number | null;
  beneficiere_id: number | null;
  beneficiere_type: 'local' | 'foreign';
  amount: number;
  motif: string;
  remarque: string | null;
  attachement: string | null;
  created_at: string;
  updated_at: string;
  beneficiere?: LocalBeneficiary | ForeignBeneficiary | null;
  company?: Company|null;
}

export interface LocalBeneficiary {
  id: number;
  owner_id: number;
  name: string;
  bank_id: number;
  rib: string;
  attestation?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ForeignBeneficiary {
  id: number;
  owner_id: number;
  name: string;
  bank: string;
  iban?: string | null;
  swift?: string | null;
  attestation?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// State Interface
interface TransfersState {
  transfers: Transfer[] | null;
  localBeneficiaries: LocalBeneficiary[] | null;
  foreignBeneficiaries: ForeignBeneficiary[] | null;
  originalTransfers: Transfer[] | null;
  loading: boolean;
  error: string | null;
}

// Initial State
const initialState: TransfersState = {
  transfers: null,
  localBeneficiaries: null,
  foreignBeneficiaries: null,
  originalTransfers: null,
  loading: false,
  error: null,
};

// Async Thunks

// Fetch Transfers
// export const fetchTransfers = createAsyncThunk<Transfer[], void, { rejectValue: string }>(
//   'transfers/fetchTransfers',
//   async (_, { rejectWithValue }) => {
//     try {
//       const accessToken = localStorage.getItem('lbankaAccessToken');
//       const headers = {
//         'Content-Type': 'application/json',
//         ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
//       };

//       const response = await fetch(`${HOST_API}/fetchTransfers`, { headers });

//       if (!response.ok) {
//         throw new Error('Failed to fetch transfers');
//       }

//       const data: Transfer[] = await response.json();
//       return data;
//     } catch (error) {
//       return rejectWithValue((error as Error).message);
//     }
//   }
// );
export const fetchTransfers = createAsyncThunk<Transfer[], Transfer[], { rejectValue: string }>(
  'transfers/fetchTransfers',
  async (transfers, { rejectWithValue }) => {
    try {
      return transfers; // Simply return the transfers passed as an argument
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// Fetch Local Beneficiaries
// export const fetchLocalBeneficiaries = createAsyncThunk<LocalBeneficiary[], void, { rejectValue: string }>(
//   'transfers/fetchLocalBeneficiaries',
//   async (_, { rejectWithValue }) => {
//     try {
//       const accessToken = localStorage.getItem('lbankaAccessToken');
//       const headers = {
//         'Content-Type': 'application/json',
//         ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
//       };

//       const response = await fetch(`${HOST_API}/fetchLocalBeneficiaries`, { headers });

//       if (!response.ok) {
//         throw new Error('Failed to fetch local beneficiaries');
//       }

//       const data: LocalBeneficiary[] = await response.json();
//       return data;
//     } catch (error) {
//       return rejectWithValue((error as Error).message);
//     }
//   }
// );
export const fetchLocalBeneficiaries = createAsyncThunk<LocalBeneficiary[], LocalBeneficiary[], { rejectValue: string }>(
  'transfers/fetchLocalBeneficiaries',
  async (localBeneficiaries, { rejectWithValue }) => {
    try {
      return localBeneficiaries; // Simply return the local beneficiaries passed as an argument
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// Fetch Foreign Beneficiaries
// export const fetchForeignBeneficiaries = createAsyncThunk<ForeignBeneficiary[], void, { rejectValue: string }>(
//   'transfers/fetchForeignBeneficiaries',
//   async (_, { rejectWithValue }) => {
//     try {
//       const accessToken = localStorage.getItem('lbankaAccessToken');
//       const headers = {
//         'Content-Type': 'application/json',
//         ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
//       };

//       const response = await fetch(`${HOST_API}/fetchForeignBeneficiaries`, { headers });

//       if (!response.ok) {
//         throw new Error('Failed to fetch foreign beneficiaries');
//       }

//       const data: ForeignBeneficiary[] = await response.json();
//       return data;
//     } catch (error) {
//       return rejectWithValue((error as Error).message);
//     }
//   }
// );

export const fetchForeignBeneficiaries = createAsyncThunk<ForeignBeneficiary[], ForeignBeneficiary[], { rejectValue: string }>(
  'transfers/fetchForeignBeneficiaries',
  async (foreignBeneficiaries, { rejectWithValue }) => {
    try {
      return foreignBeneficiaries; // Simply return the foreign beneficiaries passed as an argument
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);
export const filterTransfersBySelectedCompanyIds = createAsyncThunk<
  Transfer[], // Return type
  number[], // Argument type: selected company IDs
  { extra: { getState: () => any } } // Add `extra` with `getState`
>(
  'transfers/filterTransfersBySelectedCompanyIds',
  (selectedCompanyIds, { extra }) => {
    const state = extra.getState(); // Access state using `getState` from `extra`
    const originalTransfers = state.transfers.originalTransfers || [];

    // Filter transfers based on the passed selected company IDs
    return originalTransfers.filter((transfer: Transfer) =>
      selectedCompanyIds.includes(transfer.company_id!)
    );
  }
);



// Slice
const transfersSlice = createSlice({
  name: 'transfers',
  initialState,
  reducers: {
    // Add a new transfer
    addTransfer: (state, action: PayloadAction<{ transfer: Partial<Transfer>; selectedCompanyIds: number[] }>) => {
      if (!state.originalTransfers) {
        state.originalTransfers = [];
      }
      const newTransfer = { id: Date.now(), ...action.payload.transfer } as Transfer;
      state.originalTransfers.push(newTransfer);

      // Apply filtering to update `transfers`
      state.transfers = state.originalTransfers.filter((transfer) =>
        action.payload.selectedCompanyIds.includes(transfer.company_id!)
      );
    },

    // Update an existing transfer
    updateTransfer: (state, action: PayloadAction<{ transfer: Partial<Transfer>; selectedCompanyIds: number[] }>) => {
      if (!state.originalTransfers) {
        state.originalTransfers = [];
      }
      const index = state.originalTransfers.findIndex((t) => t.id === action.payload.transfer.id);
      if (index !== -1) {
        state.originalTransfers[index] = { ...state.originalTransfers[index], ...action.payload.transfer };
      }

      // Apply filtering to update `transfers`
      state.transfers = state.originalTransfers.filter((transfer) =>
        action.payload.selectedCompanyIds.includes(transfer.company_id!)
      );
    },

    // Remove an existing transfer
    removeTransfer: (state, action: PayloadAction<{ transferId: number; selectedCompanyIds: number[] }>) => {
      if (state.originalTransfers) {
        state.originalTransfers = state.originalTransfers.filter((t) => t.id !== action.payload.transferId);
      }

      // Apply filtering to update `transfers`
      state.transfers = state.originalTransfers?.filter((transfer) =>
        action.payload.selectedCompanyIds.includes(transfer.company_id!)
      ) || [];
    },

    // // Add a new transfer
    // addTransfer: (state, action: PayloadAction<Partial<Transfer>>) => {
    //   if (!state.originalTransfers) {
    //     state.originalTransfers = [];
    //   }
    //   const newTransfer = { id: Date.now(), ...action.payload } as Transfer;
    //   state.originalTransfers.push(newTransfer);
    // },

    // // Update an existing transfer
    // updateTransfer: (state, action: PayloadAction<Partial<Transfer>>) => {
    //   if (!state.originalTransfers) {
    //     state.originalTransfers = [];
    //   }
    //   const index = state.originalTransfers.findIndex((t) => t.id === action.payload.id);
    //   if (index !== -1) {
    //     state.originalTransfers[index] = { ...state.originalTransfers[index], ...action.payload };
    //   }
    // },

    // // Remove an existing transfer
    // removeTransfer: (state, action: PayloadAction<number>) => {
    //   if (state.originalTransfers) {
    //     state.originalTransfers = state.originalTransfers.filter((t) => t.id !== action.payload);
    //   }
    // },


    // Add a local beneficiary
    addLocalBeneficiary: (state, action: PayloadAction<Partial<LocalBeneficiary>>) => {
      if (!state.localBeneficiaries) {
        state.localBeneficiaries = [];
      }
      const newLocalBeneficiary = { id: Date.now(), ...action.payload } as LocalBeneficiary;
      state.localBeneficiaries.push(newLocalBeneficiary);
    },

    // Update an existing local beneficiary
    updateLocalBeneficiary: (state, action: PayloadAction<Partial<LocalBeneficiary>>) => {
      if (!state.localBeneficiaries) {
        state.localBeneficiaries = [];
      }
      const index = state.localBeneficiaries.findIndex((b) => b.id === action.payload.id);
      if (index !== -1) {
        state.localBeneficiaries[index] = {
          ...state.localBeneficiaries[index],
          ...action.payload,
        };
      }
    },

    // Remove an existing local beneficiary
    removeLocalBeneficiary: (state, action: PayloadAction<number>) => {
      if (state.localBeneficiaries) {
        state.localBeneficiaries = state.localBeneficiaries.filter((b) => b.id !== action.payload);
      }
    },

    // Add a foreign beneficiary
    addForeignBeneficiary: (state, action: PayloadAction<Partial<ForeignBeneficiary>>) => {
      if (!state.foreignBeneficiaries) {
        state.foreignBeneficiaries = [];
      }
      const newForeignBeneficiary = { id: Date.now(), ...action.payload } as ForeignBeneficiary;
      state.foreignBeneficiaries.push(newForeignBeneficiary);
    },

    // Update an existing foreign beneficiary
    updateForeignBeneficiary: (state, action: PayloadAction<Partial<ForeignBeneficiary>>) => {
      if (!state.foreignBeneficiaries) {
        state.foreignBeneficiaries = [];
      }
      const index = state.foreignBeneficiaries.findIndex((b) => b.id === action.payload.id);
      if (index !== -1) {
        state.foreignBeneficiaries[index] = {
          ...state.foreignBeneficiaries[index],
          ...action.payload,
        };
      }
    },

    // Remove an existing foreign beneficiary
    removeForeignBeneficiary: (state, action: PayloadAction<number>) => {
      if (state.foreignBeneficiaries) {
        state.foreignBeneficiaries = state.foreignBeneficiaries.filter((b) => b.id !== action.payload);
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch Transfers
      .addCase(fetchTransfers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransfers.fulfilled, (state, action: PayloadAction<Transfer[]>) => {
        state.originalTransfers = action.payload; // Store original data
        state.transfers = action.payload; // Initialize filtered data
        state.loading = false;
      })
      .addCase(fetchTransfers.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.transfers = [];
        state.originalTransfers = [];
        state.loading = false;
        state.error = action.payload || 'Failed to fetch transfers.';
      })

      // Fetch Local Beneficiaries
      .addCase(fetchLocalBeneficiaries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLocalBeneficiaries.fulfilled, (state, action: PayloadAction<LocalBeneficiary[]>) => {
        state.localBeneficiaries = action.payload.length ? action.payload : [];
        state.loading = false;
      })
      .addCase(fetchLocalBeneficiaries.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.localBeneficiaries = [];
        state.loading = false;
        state.error = action.payload || 'Failed to fetch local beneficiaries.';
      })

      // Fetch Foreign Beneficiaries
      .addCase(fetchForeignBeneficiaries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchForeignBeneficiaries.fulfilled, (state, action: PayloadAction<ForeignBeneficiary[]>) => {
        state.foreignBeneficiaries = action.payload.length ? action.payload : [];
        state.loading = false;
      })
      .addCase(fetchForeignBeneficiaries.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.foreignBeneficiaries = [];
        state.loading = false;
        state.error = action.payload || 'Failed to fetch foreign beneficiaries.';
      })

      // Filter Transfers Based on Selected Company IDs
      .addCase(filterTransfersBySelectedCompanyIds.fulfilled, (state, action: PayloadAction<Transfer[]>) => {
        state.transfers = action.payload; // Update filtered transfers
      })
      // Filter Data Based on Selected Company IDs
      .addCase(setSelectedCompanyIds, (state, action: PayloadAction<number[]>) => {
        if (state.originalTransfers) {
          state.transfers = state.originalTransfers.filter((transfer) =>
            action.payload.includes(transfer.company_id!)
          );
        }
      });
  },
});

// Export Actions
export const {
  addTransfer,
  updateTransfer,
  addLocalBeneficiary,
  updateLocalBeneficiary,
  removeLocalBeneficiary,
  addForeignBeneficiary,
  updateForeignBeneficiary,
  removeForeignBeneficiary,
} = transfersSlice.actions;

// Export Reducer
export default transfersSlice.reducer;
